import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Edit, GripVertical, Save, X, Download, 
  UserCog, Users, ShoppingCart, FileText, ChevronDown, ChevronRight, Copy, RefreshCw
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import usePageTitle from '../hooks/usePageTitle';

const FIELD_TYPES = [
  { value: 'text', label: 'Single Line Text' },
  { value: 'textarea', label: 'Paragraph Text' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'price', label: 'Price (USD)' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'note', label: 'Notes / Instructions' }
];

const CATEGORY_CONFIG = {
  registration: { 
    icon: Users, 
    label: 'Registration Questions', 
    description: 'Questions for user registration during account creation',
    color: '#0EA5E9'
  },
  broker: { 
    icon: UserCog, 
    label: 'Broker Questions', 
    description: 'Questions for broker verification and onboarding',
    color: '#2B4B6A'
  },
  buyer: { 
    icon: ShoppingCart, 
    label: 'Buyer Questions', 
    description: 'Questions for buyer qualification',
    color: '#059669'
  },
  seller: { 
    icon: Users, 
    label: 'Seller Questions', 
    description: 'Questions for seller listing',
    color: '#7C3AED'
  }
};

const CompanyQuestions = () => {
  usePageTitle('Manage Questions');
  const { user } = useAuth();
  const { adminTheme } = useTheme();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({ registration: true, broker: true, buyer: true, seller: true });
  
  const [formData, setFormData] = useState({
    question_text: '',
    field_type: 'text',
    question_category: 'broker',
    options: [],
    optionsText: '',
    allow_multiple: false,
    is_required: true,
    display_order: 0,
    description: '',
    allow_document_upload: false,
    require_document_upload: false
  });

  const primaryColor = adminTheme?.buttons?.primaryColor || '#2B4B6A';
  const [coreTemplates, setCoreTemplates] = useState([]);

  useEffect(() => {
    fetchQuestions();
    fetchCoreTemplates();
  }, []);

  const fetchCoreTemplates = async () => {
    try {
      const res = await api.get('/questions/core-templates');
      setCoreTemplates(res.data);
    } catch (error) {
      console.error('Failed to load core templates:', error);
    }
  };

  const getCoreTemplatesByCategory = (category) => {
    return coreTemplates.filter(t => t.question_category === category)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      // Fetch questions for all categories
      const categories = ['registration', 'broker', 'buyer', 'seller'];
      const allQuestions = [];
      
      for (const category of categories) {
        const res = await api.get(`/questions?category=${category}`);
        allQuestions.push(...res.data);
      }
      
      setQuestions(allQuestions);
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultQuestions = async (category) => {
    setLoadingDefaults(true);
    try {
      const res = await api.post('/company/settings/load-default-questions', {
        category,
        replace_existing: false
      });
      toast.success(res.data.message);
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load default questions');
    } finally {
      setLoadingDefaults(false);
    }
  };

  const loadAllDefaults = async () => {
    if (!window.confirm('This will load all default questions from the platform templates. Existing questions will not be replaced. Continue?')) {
      return;
    }
    
    setLoadingDefaults(true);
    try {
      for (const category of ['company_registration', 'registration', 'broker', 'buyer', 'seller']) {
        await api.post('/company/settings/load-default-questions', {
          category,
          replace_existing: false
        });
      }
      toast.success('All default questions loaded successfully');
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load default questions');
    } finally {
      setLoadingDefaults(false);
    }
  };

  const handleSave = async () => {
    if (!formData.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        options: formData.field_type === 'select' || formData.field_type === 'checkbox'
          ? formData.optionsText.split('\n').map(o => o.trim()).filter(Boolean)
          : []
      };

      if (editingQuestion) {
        await api.put(`/questions/${editingQuestion.id}`, payload);
        toast.success('Question updated');
      } else {
        await api.post('/questions', payload);
        toast.success('Question created');
      }
      
      setShowDialog(false);
      resetForm();
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save question');
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      question_text: question.question_text,
      field_type: question.field_type,
      question_category: question.question_category,
      options: question.options || [],
      optionsText: (question.options || []).join('\n'),
      allow_multiple: question.allow_multiple || false,
      is_required: question.is_required ?? true,
      display_order: question.display_order || 0,
      description: question.description || '',
      allow_document_upload: question.allow_document_upload || false,
      require_document_upload: question.require_document_upload || false
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await api.delete(`/questions/${id}`);
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleDuplicate = async (question) => {
    try {
      await api.post('/questions', {
        question_text: `${question.question_text} (Copy)`,
        field_type: question.field_type,
        question_category: question.question_category,
        options: question.options || [],
        allow_multiple: question.allow_multiple || false,
        is_required: question.is_required ?? true,
        display_order: (question.display_order || 0) + 1,
        description: question.description || ''
      });
      toast.success('Question duplicated');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to duplicate question');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const category = result.source.droppableId;
    const categoryQuestions = questions.filter(q => q.question_category === category);
    const [reorderedItem] = categoryQuestions.splice(result.source.index, 1);
    categoryQuestions.splice(result.destination.index, 0, reorderedItem);
    
    // Update local state immediately
    const otherQuestions = questions.filter(q => q.question_category !== category);
    const updatedQuestions = categoryQuestions.map((q, index) => ({
      ...q,
      display_order: index
    }));
    setQuestions([...otherQuestions, ...updatedQuestions]);
    
    // Save order to backend
    try {
      await api.put('/questions/order', {
        category,
        order: categoryQuestions.map((q, index) => ({ id: q.id, display_order: index }))
      });
    } catch (error) {
      toast.error('Failed to save order');
      fetchQuestions();
    }
  };

  const resetForm = () => {
    setEditingQuestion(null);
    setFormData({
      question_text: '',
      field_type: 'text',
      question_category: 'broker',
      options: [],
      optionsText: '',
      allow_multiple: false,
      is_required: true,
      display_order: 0,
      description: '',
      allow_document_upload: false,
      require_document_upload: false
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getQuestionsByCategory = (category) => {
    return questions
      .filter(q => q.question_category === category)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Question Templates</h1>
              <p className="text-gray-500 text-sm mt-1">
                Manage questions for registration, brokers, buyers, and sellers
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={loadAllDefaults}
                disabled={loadingDefaults}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                data-testid="load-all-defaults-btn"
              >
                <Download className="w-4 h-4" />
                {loadingDefaults ? 'Loading...' : 'Load Platform Defaults'}
              </button>
              <button
                onClick={() => {
                  resetForm();
                  setShowDialog(true);
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
                data-testid="add-question-btn"
              >
                <Plus className="w-4 h-4" />
                Add Question
              </button>
            </div>
          </div>

          {/* Questions by Category */}
          <DragDropContext onDragEnd={handleDragEnd}>
            {Object.entries(CATEGORY_CONFIG).map(([category, config]) => {
              const CategoryIcon = config.icon;
              const categoryQuestions = getQuestionsByCategory(category);
              const categoryCoreTemplates = getCoreTemplatesByCategory(category);
              const isExpanded = expandedCategories[category];
              
              return (
                <div key={category} className="mb-4">
                  <div
                    className="flex items-center justify-between p-4 bg-white rounded-t-xl border border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleCategory(category)}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${config.color}15` }}
                      >
                        <CategoryIcon className="w-5 h-5" style={{ color: config.color }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{config.label}</h3>
                        <p className="text-xs text-gray-500">
                          {categoryCoreTemplates.length > 0
                            ? `${categoryCoreTemplates.length} core + ${categoryQuestions.length} custom`
                            : `${categoryQuestions.length} question${categoryQuestions.length !== 1 ? 's' : ''}`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          loadDefaultQuestions(category);
                        }}
                        disabled={loadingDefaults}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <RefreshCw className={`w-3 h-3 ${loadingDefaults ? 'animate-spin' : ''}`} />
                        Load Defaults
                      </button>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="bg-white border-x border-b border-gray-200 rounded-b-xl">
                      {/* Core Questions Section - from database */}
                      {categoryCoreTemplates.length > 0 && (
                        <div className="bg-slate-50 border-b border-gray-200">
                          <div className="px-4 py-2 bg-slate-100 border-b border-gray-200">
                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-2">
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              Core System Fields (Managed by Super Admin)
                            </p>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {categoryCoreTemplates.map((template, idx) => (
                              <div 
                                key={template.id}
                                className="flex items-center gap-3 p-4 bg-slate-50/50"
                              >
                                <div className="text-gray-300 cursor-not-allowed">
                                  <GripVertical className="w-4 h-4" />
                                </div>
                                <span className="text-sm font-mono text-gray-400 w-6">{idx + 1}.</span>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-700 flex items-center gap-2">
                                    {template.question_text}
                                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-normal">Core</span>
                                    {template.step && (
                                      <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-normal">
                                        {template.step}
                                      </span>
                                    )}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">
                                      {FIELD_TYPES.find(f => f.value === template.field_type)?.label || template.field_type}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      template.is_required 
                                        ? 'bg-red-100 text-red-600' 
                                        : 'bg-gray-100 text-gray-500'
                                    }`}>
                                      {template.is_required ? 'Required' : 'Optional'}
                                    </span>
                                    {template.options?.length > 0 && (
                                      <span className="text-xs text-gray-500">
                                        {template.options.length} options
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-xs text-gray-400 italic">
                                  System field
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Custom Questions Header */}
                      {categoryCoreTemplates.length > 0 && (
                        <div className="px-4 py-2 bg-orange-50 border-b border-orange-200">
                          <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide flex items-center gap-2">
                            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                            Custom Questions (Editable)
                          </p>
                        </div>
                      )}

                      <Droppable droppableId={category}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`min-h-[50px] ${snapshot.isDraggingOver ? 'bg-blue-50' : ''}`}
                          >
                            {categoryQuestions.length === 0 ? (
                              <div className="p-8 text-center text-gray-500">
                                <p>No custom questions configured for this category.</p>
                                <button
                                  onClick={() => loadDefaultQuestions(category)}
                                  className="mt-2 text-sm text-blue-600 hover:underline"
                                >
                                  Load default questions from platform
                                </button>
                              </div>
                            ) : (
                              categoryQuestions.map((question, index) => (
                                <Draggable key={question.id} draggableId={question.id} index={index}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`flex items-center gap-3 p-4 border-b border-gray-100 last:border-b-0 ${
                                        snapshot.isDragging ? 'bg-blue-50 shadow-lg' : 'hover:bg-gray-50'
                                      }`}
                                    >
                                      <div
                                        {...provided.dragHandleProps}
                                        className={`${question.is_core ? 'cursor-not-allowed opacity-30' : 'cursor-grab active:cursor-grabbing'} text-gray-400 hover:text-gray-600`}
                                      >
                                        <GripVertical className="w-5 h-5" />
                                      </div>
                                      
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                                          {question.question_text}
                                          {question.is_core && (
                                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-normal">Core</span>
                                          )}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                            {FIELD_TYPES.find(t => t.value === question.field_type)?.label || question.field_type}
                                          </span>
                                          {question.is_required && (
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                                              Required
                                            </span>
                                          )}
                                          {question.allow_document_upload && (
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-600">
                                              📎 Upload
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => handleDuplicate(question)}
                                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                          title="Duplicate"
                                        >
                                          <Copy className="w-4 h-4" />
                                        </button>
                                        {!question.is_core && (
                                          <>
                                            <button
                                              onClick={() => handleEdit(question)}
                                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                              title="Edit"
                                            >
                                              <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                              onClick={() => handleDelete(question.id)}
                                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                              title="Delete"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  )}
                </div>
              );
            })}
          </DragDropContext>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion ? 'Edit Question' : 'Add Question'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Question Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question Text *
              </label>
              <textarea
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows={3}
                placeholder="Enter the question..."
                data-testid="question-text-input"
              />
            </div>

            {/* Category & Field Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={formData.question_category}
                  onChange={(e) => setFormData({ ...formData, question_category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="question-category-select"
                >
                  <option value="registration">Registration</option>
                  <option value="broker">Broker</option>
                  <option value="buyer">Buyer</option>
                  <option value="seller">Seller</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Field Type
                </label>
                <select
                  value={formData.field_type}
                  onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="question-field-type-select"
                >
                  {FIELD_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Options (for select/checkbox) */}
            {(formData.field_type === 'select' || formData.field_type === 'checkbox') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Options (one per line)
                </label>
                <textarea
                  value={formData.optionsText}
                  onChange={(e) => setFormData({ ...formData, optionsText: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  rows={4}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                  data-testid="question-options-input"
                />
              </div>
            )}

            {/* Checkboxes */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_required}
                  onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-700">Required</span>
              </label>
              {formData.field_type === 'checkbox' && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.allow_multiple}
                    onChange={(e) => setFormData({ ...formData, allow_multiple: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Allow multiple selections</span>
                </label>
              )}
            </div>

            {/* Allow Document Upload */}
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.allow_document_upload}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    allow_document_upload: e.target.checked,
                    require_document_upload: e.target.checked ? formData.require_document_upload : false
                  })}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  data-testid="allow-document-upload-checkbox"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">Allow Document Upload</span>
                  <p className="text-xs text-gray-500 mt-0.5">Users can upload files (ID, passport, certificates, etc.)</p>
                </div>
              </label>
              
              {formData.allow_document_upload && (
                <label className="flex items-center gap-3 cursor-pointer mt-3 ml-8 pt-3 border-t border-gray-200">
                  <input
                    type="checkbox"
                    checked={formData.require_document_upload}
                    onChange={(e) => setFormData({ ...formData, require_document_upload: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    data-testid="require-document-upload-checkbox"
                  />
                  <div>
                    <span className="text-sm font-medium text-red-700">Require Document</span>
                    <p className="text-xs text-gray-500 mt-0.5">Document upload is mandatory for this question</p>
                  </div>
                </label>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => {
                  setShowDialog(false);
                  resetForm();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 flex items-center gap-2"
                style={{ backgroundColor: primaryColor }}
                data-testid="save-question-btn"
              >
                <Save className="w-4 h-4" />
                {editingQuestion ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
};

export default CompanyQuestions;
